import { useCallback, useState } from 'react';
import type { GameSnapshot, ServerMessage } from '../types';

// Points are derived from submitted_answers (minus known wrong-guess
// penalties), mirroring the server's tally, so incremental word messages
// can't drift or double-count.
function withRecomputedScores(snapshot: GameSnapshot): GameSnapshot {
  const players = snapshot.players.map((player) => {
    let points = 0;
    let wordsCompleted = 0;
    for (const submission of Object.values(snapshot.submitted_answers)) {
      if (submission.submitted_by === player.id && submission.is_correct) {
        points += submission.points;
        wordsCompleted += 1;
      }
      if (submission.second_submission === player.id) {
        points += submission.second_points ?? 0;
        wordsCompleted += 1;
      }
    }
    if (snapshot.mode === 'competitive') {
      points -= player.incorrect_guesses ?? 0;
    }
    return { ...player, points, words_completed: wordsCompleted };
  });
  return { ...snapshot, players };
}

export function useGameState() {
  const [snapshot, setSnapshot] = useState<GameSnapshot | null>(null);
  const [lastMessage, setLastMessage] = useState<string>('');

  const applyMessage = useCallback((message: ServerMessage) => {
    setLastMessage(message.type);
    if (message.type === 'game_update') {
      if (message.full && message.game) {
        setSnapshot(message.game as GameSnapshot);
        return;
      }
      setSnapshot((current) => {
        if (!current) return current;
        return {
          ...current,
          submitted_answers: {
            ...current.submitted_answers,
            ...((message.submitted_answers as GameSnapshot['submitted_answers'] | undefined) ?? {}),
          },
          players: (message.players as GameSnapshot['players'] | undefined) ?? current.players,
          current_turn:
            (message.current_turn as GameSnapshot['current_turn'] | undefined) ?? current.current_turn,
          ended_at: (message.ended_at as string | null | undefined) ?? current.ended_at,
        };
      });
      return;
    }

    if (message.type === 'word_submitted' && message.is_correct === true) {
      setSnapshot((current) => {
        if (!current) return current;
        const wordId = message.word_id as string;
        const previous = current.submitted_answers[wordId];
        return withRecomputedScores({
          ...current,
          submitted_answers: {
            ...current.submitted_answers,
            [wordId]: {
              answer: (message.answer as string) ?? previous?.answer ?? '',
              submitted_by: message.submitted_by as string,
              submitted_at: (message.submitted_at as string) ?? message.timestamp,
              is_correct: true,
              points: (message.points_awarded as number) ?? 0,
            },
          },
        });
      });
      return;
    }

    if (message.type === 'word_partial_points') {
      setSnapshot((current) => {
        if (!current) return current;
        const wordId = message.word_id as string;
        const existing = current.submitted_answers[wordId];
        if (!existing) return current;
        return withRecomputedScores({
          ...current,
          submitted_answers: {
            ...current.submitted_answers,
            [wordId]: {
              ...existing,
              second_submission: message.second_player as string,
              second_submitted_at: (message.submitted_at as string) ?? message.timestamp,
              second_points: (message.second_points as number) ?? 0,
            },
          },
        });
      });
      return;
    }

    if (message.type === 'player_ready') {
      setSnapshot((current) => {
        if (!current) return current;
        return {
          ...current,
          players: current.players.map((player) =>
            player.id === message.player_id ? { ...player, ready: true } : player
          ),
        };
      });
      return;
    }

    if (message.type === 'game_started') {
      setSnapshot((current) =>
        current
          ? {
              ...current,
              phase: 'active',
              started_at: (message.started_at as string) ?? current.started_at,
            }
          : current
      );
      return;
    }

    if (message.type === 'turn_changed') {
      setSnapshot((current) =>
        current
          ? {
              ...current,
              current_turn: {
                player_id: message.player_id as string,
                expires_at: message.expires_at as string,
              },
            }
          : current
      );
      return;
    }

    if (message.type === 'player_left') {
      setSnapshot((current) => {
        if (!current) return current;
        return {
          ...current,
          players: current.players.map((player) =>
            player.id === message.player_id && player.status === 'active'
              ? { ...player, status: 'away' }
              : player
          ),
        };
      });
      return;
    }

    if (message.type === 'player_joined' || message.type === 'player_status_changed') {
      setSnapshot((current) => {
        if (!current) return current;
        if (message.player_id && message.type === 'player_status_changed') {
          return {
            ...current,
            players: current.players.map((player) =>
              player.id === message.player_id
                ? { ...player, status: message.status as GameSnapshot['players'][number]['status'] }
                : player
            ),
          };
        }
        if (message.type === 'player_joined' && message.player_id) {
          const exists = current.players.some((player) => player.id === message.player_id);
          if (exists) return current;
          return {
            ...current,
            players: [
              ...current.players,
              {
                id: message.player_id as string,
                name: (message.name as string) ?? 'Player',
                status: 'active',
                ready: false,
                joined_at: message.timestamp,
                points: 0,
                words_completed: 0,
                words_attempted: 0,
                incorrect_guesses: 0,
                done_at: null,
              },
            ],
          };
        }
        return current;
      });
    }

    if (message.type === 'game_ended') {
      setSnapshot((current) => {
        if (!current) return current;
        const finalScores = message.final_scores as Array<GameSnapshot['players'][number] & { rank: number }> | undefined;
        return {
          ...current,
          players: finalScores ?? current.players,
          submitted_answers:
            (message.submitted_answers as GameSnapshot['submitted_answers'] | undefined) ??
            current.submitted_answers,
          current_turn: null,
          ended_at: current.ended_at ?? message.timestamp,
        };
      });
    }
  }, []);

  return { snapshot, setSnapshot, applyMessage, lastMessage };
}
